import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendIcon from "@material-ui/icons/Send";
import EditIcon from "@material-ui/icons/Edit";
import RepeatIcon from "@material-ui/icons/Repeat";
import VisibilityIcon from "@material-ui/icons/Visibility";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import WhatsAppStatusModal from "../../components/WhatsAppStatusModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { format, parseISO, isPast } from "date-fns";
import {
    getWhatsAppStatuses,
    sendWhatsAppStatus,
    deleteWhatsAppStatus,
    republishWhatsAppStatus
} from "../../services/whatsappStatus";
import api from "../../services/api";

const reducer = (state, action) => {
    if (action.type === "LOAD_STATUSES") {
        const statuses = action.payload;
        return [...statuses];
    }

    if (action.type === "UPDATE_STATUS") {
        const status = action.payload;
        const statusIndex = state.findIndex(s => s.id === status.id);

        if (statusIndex !== -1) {
            state[statusIndex] = status;
            return [...state];
        } else {
            return [status, ...state];
        }
    }

    if (action.type === "DELETE_STATUS") {
        const statusId = parseInt(action.payload);
        return state.filter(s => s.id !== statusId);
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    filterContainer: {
        display: "flex",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    formControl: {
        minWidth: 200,
    },
}));

const getStatusColor = (status) => {
    const colors = {
        pending: "default",
        sent: "primary",
        error: "secondary"
    };
    return colors[status] || "default";
};

const getTypeLabel = (type) => {
    const labels = {
        text: "Texto",
        image: "Imagem",
        video: "Vídeo",
        audio: "Áudio",
        myaudio: "Áudio",
        ptt: "Áudio (PTT)"
    };
    return labels[type] || type;
};

const WhatsAppStatus = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [statuses, dispatch] = useReducer(reducer, []);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [whatsapps, setWhatsapps] = useState([]);
    const [filterWhatsappId, setFilterWhatsappId] = useState("");
    const [filterType, setFilterType] = useState("");

    useEffect(() => {
        setLoading(true);
        const fetchStatuses = async () => {
            try {
                const params = {};
                if (filterWhatsappId) params.whatsappId = filterWhatsappId;
                if (filterType) params.type = filterType;

                const data = await getWhatsAppStatuses(params);
                dispatch({ type: "LOAD_STATUSES", payload: data });
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        };
        fetchStatuses();
    }, [filterWhatsappId, filterType]);

    useEffect(() => {
        const fetchWhatsapps = async () => {
            try {
                const { data } = await api.get("/whatsapp");
                setWhatsapps(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchWhatsapps();
    }, []);

    useEffect(() => {
        const socket = openSocket();

        socket.on("whatsappStatus", data => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_STATUS", payload: data.status });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_STATUS", payload: data.statusId });
            }
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const handleOpenStatusModal = () => {
        setSelectedStatus(null);
        setStatusModalOpen(true);
    };

    const handleCloseStatusModal = () => {
        setSelectedStatus(null);
        setStatusModalOpen(false);
    };

    const handleEditStatus = status => {
        setSelectedStatus(status);
        setStatusModalOpen(true);
    };

    const handleDeleteStatus = async statusId => {
        try {
            await deleteWhatsAppStatus(statusId);
            toast.success("Status deletado com sucesso");
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    const handleSendStatus = async statusId => {
        try {
            await sendWhatsAppStatus(statusId);
            toast.success("Status enviado com sucesso");
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    const handleRepublishStatus = async statusId => {
        try {
            await republishWhatsAppStatus(statusId);
            toast.success("Status republicado com sucesso");
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    const openConfirmModal = (action, status) => {
        setConfirmAction({ action, status });
        setConfirmModalOpen(true);
    };

    return (
        <MainContainer>
            <WhatsAppStatusModal
                open={statusModalOpen}
                onClose={handleCloseStatusModal}
                statusId={selectedStatus?.id}
            />
            <ConfirmationModal
                title={
                    confirmAction?.action === "delete"
                        ? "Deletar Status"
                        : confirmAction?.action === "send"
                            ? "Enviar Status"
                            : "Republicar Status"
                }
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => {
                    if (confirmAction?.action === "delete") {
                        handleDeleteStatus(confirmAction.status.id);
                    } else if (confirmAction?.action === "send") {
                        handleSendStatus(confirmAction.status.id);
                    } else if (confirmAction?.action === "republish") {
                        handleRepublishStatus(confirmAction.status.id);
                    }
                }}
            >
                {confirmAction?.action === "delete"
                    ? "Tem certeza que deseja deletar este status?"
                    : confirmAction?.action === "send"
                        ? "Tem certeza que deseja enviar este status para o WhatsApp?"
                        : "Tem certeza que deseja republicar este status? Um novo status será criado com o mesmo conteúdo."}
            </ConfirmationModal>
            <MainHeader>
                <Title>Status WhatsApp</Title>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenStatusModal}
                >
                    Novo Status
                </Button>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <div className={classes.filterContainer}>
                    <FormControl className={classes.formControl}>
                        <InputLabel>WhatsApp</InputLabel>
                        <Select
                            value={filterWhatsappId}
                            onChange={e => setFilterWhatsappId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {whatsapps.map(whatsapp => (
                                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                    {whatsapp.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="text">Texto</MenuItem>
                            <MenuItem value="image">Imagem</MenuItem>
                            <MenuItem value="video">Vídeo</MenuItem>
                            <MenuItem value="audio">Áudio</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Tipo</TableCell>
                            <TableCell align="center">Conteúdo</TableCell>
                            <TableCell align="center">WhatsApp</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Agendado para</TableCell>
                            <TableCell align="center">Criado em</TableCell>
                            <TableCell align="center">Enviado em</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {statuses.map(status => (
                                <TableRow key={status.id}>
                                    <TableCell align="center">
                                        {getTypeLabel(status.type)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {status.text ?
                                            (status.text.length > 50
                                                ? status.text.substring(0, 50) + "..."
                                                : status.text)
                                            : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {status.whatsapp?.name || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={status.status === "pending" ? "Pendente/Agendado" : status.status === "sent" ? "Enviado" : "Erro"}
                                            color={getStatusColor(status.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {status.scheduledAt
                                            ? format(parseISO(status.scheduledAt), "dd/MM/yyyy HH:mm")
                                            : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {format(parseISO(status.createdAt), "dd/MM/yyyy HH:mm")}
                                    </TableCell>
                                    <TableCell align="center">
                                        {status.sentAt
                                            ? format(parseISO(status.sentAt), "dd/MM/yyyy HH:mm")
                                            : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {status.status === "pending" && (
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => handleEditStatus(status)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {status.status === "sent" && status.expiresAt && isPast(parseISO(status.expiresAt)) && (
                                            <Tooltip title="Republicar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openConfirmModal("republish", status)}
                                                    color="primary"
                                                >
                                                    <RepeatIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Deletar">
                                            <IconButton
                                                size="small"
                                                onClick={() => openConfirmModal("delete", status)}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={8} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default WhatsAppStatus;
